<template>
    <div class="sequences-tab pl-4 pt-3 row nav-padding"
         v-loading="loading">
        <el-form class="w-50"
                 label-width="130px"
                 label-position="top"
                 ref="transfer_numbers"
                 :model="transfer_numbers"
                 :rules="form_rules">
            <el-form-item label="Phone Numbers"
                          prop="phone_numbers">
                <el-input type="textarea"
                          rows="5"
                          placeholder="Enter phone numbers seperated by space or enter each phone number in a new line. e.g 855-539-2287 956-539-5884 405-847-9404"
                          v-model="transfer_numbers.phone_numbers">
                </el-input>
            </el-form-item>

            <el-form-item label="Source"
                          prop="from_company">
                <div>
                    <small class="font-weight-light">
                        <i class="fa fa-exclamation-circle text-info"></i> In case source is not given, numbers will be moved from the master account
                    </small>
                </div>
                <company-autosuggest class="w-50"
                                     placeholder="Search for the company to transfer numbers from (optional)"
                                     v-model="transfer_numbers.from_company"
                                     @company-selected="setFromCompanyLabel">
                </company-autosuggest>
            </el-form-item>

            <el-form-item label="Destination"
                          prop="to_company">
                <company-autosuggest class="w-50"
                                     placeholder="Search for the company to transfer the numbers to"
                                     v-model="transfer_numbers.to_company"
                                     @company-selected="setToCompanyLabel">
                </company-autosuggest>
            </el-form-item>

            <el-form-item label="Destination Campaign (Optional)"
                          prop="to_campaign">
                <el-select v-model="transfer_numbers.to_campaign"
                        class="w-50"
                        autocomplete="off"
                        placeholder="Select a campaign"
                        filterable
                        clearable>
                    <el-option v-for="campaign in campaigns"
                            :key="campaign.id"
                            :label="campaign.name"
                            :value="campaign.id">
                        <span>{{ campaign.name }}</span>
                    </el-option>
                </el-select>
            </el-form-item>

            <el-form-item label="Address SID (Optional)"
                          prop="address_sid">
                <div>
                    <small class="font-weight-light">
                        <i class="fa fa-exclamation-circle text-info"></i> This is needed for transferring international numbers
                    </small>
                </div>
                <el-input v-model="transfer_numbers.address_sid"></el-input>
            </el-form-item>

            <el-form-item>
                <el-button type="success"
                           @click="submitForm">
                    Transfer
                </el-button>
            </el-form-item>
        </el-form>

        <el-dialog width="40%"
                   :title="dialogTitle"
                   :visible.sync="showResultDialog"
                    @close="resetForm">
            <div class="container text-dark">
                <div class="px-2"
                     v-if="resultHasTransferred">
                    <h6 class="font-weight-bold mb-0">Transferred</h6>
                    <div class="mb-1">
                        <small class="font-weight-light py-1">
                            These are successfully transferred phone numbers in Twilio and company sub accounts.
                        </small>
                    </div>
                    <div class="break-word">
                        {{ transfer_result.transferred.join(', ') }}
                    </div>
                </div>
                <div class="px-2"
                     :class="{'mt-4': resultHasTransferred}"
                     v-if="resultHasUnprocessed">
                    <h6 class="font-weight-bold mb-0">Unprocessed</h6>
                    <div class="mb-1">
                        <small class="font-weight-light py-1">
                            These are the incoming numbers that are not in Twilio incoming number list.
                        </small>
                    </div>
                    <div class="break-word">
                        {{ transfer_result.unprocessed.join(', ') }}
                    </div>
                </div>
                <div class="px-2"
                     :class="{'mt-4': (resultHasTransferred || resultHasUnprocessed)}"
                     v-if="resultHasErrors">
                    <h6 class="font-weight-bold mb-0">Failed</h6>
                    <div class="mb-1">
                        <small class="font-weight-light py-1">
                            These are phone numbers that encountered error while transferring.
                        </small>
                    </div>
                    <div class="break-word">
                        <table class="table"
                               v-if="resultHasErrors">
                            <tbody>
                                <tr v-for="(error_msg, phone_number) in transfer_result.failed"
                                    :key="phone_number">
                                    <td class="w-sm pl-0">{{ phone_number }}</td>
                                    <td class="pr-0 break-word">{{ error_msg }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>
        </el-dialog>
    </div>
</template>

<script>
import auth from '../../auth'
import CompanyAutosuggest from '../company-autosuggest'
import {form_validation_mixin} from '../../mixins'

export default {
    components: {
        CompanyAutosuggest
    },

    mixins: [
        form_validation_mixin
    ],

    data() {
        return {
            loading: false,
            auth: auth,
            form_name: 'transfer_numbers',
            transfer_numbers: {
                phone_numbers: null,
                from_company: null,
                to_company: null,
                address_sid: null,
                to_campaign: null
            },
            form_rules: {
                phone_numbers: {
                    required: true,
                    message: 'Please input phone numbers to transfer',
                    trigger: 'blur'
                },
                to_company: {
                    required: true,
                    message: 'Please input the company to transfer the numbers to',
                    trigger: 'blur'
                }
            },
            to_company_label: '',
            from_company_label: '',
            showResultDialog: false,
            transfer_result: [],
            campaigns: []
        }
    },

    computed: {
        arrayedPhoneNumbers() {
            let filtered_phone_numbers = []

            this.transfer_numbers.phone_numbers
                .replace(/\n/g, ',') // prepare new-lined numbers for array split
                .replace(/\s/g, '') // clean all possible whitespace characters. i.e. (123) 456-7890 -> (123)456-7890
                .split(',')
                .forEach(phone_number => {
                    // include trimmed number strings
                    phone_number = phone_number.trim()

                    if (phone_number) {
                       filtered_phone_numbers.push(phone_number)
                    }
                })

            return filtered_phone_numbers
        },

        resultHasTransferred() {
            if (Object.keys(this.transfer_result).length && this.transfer_result.hasOwnProperty('transferred')) {
                return Object.entries(this.transfer_result.transferred).length
            }

            return false
        },

        resultHasErrors() {
            if (Object.keys(this.transfer_result).length && this.transfer_result.hasOwnProperty('failed')) {
                return Object.entries(this.transfer_result.failed).length
            }

            return false
        },

        resultHasUnprocessed() {
            if (Object.keys(this.transfer_result).length && this.transfer_result.hasOwnProperty('unprocessed')) {
                return Object.entries(this.transfer_result.unprocessed).length
            }

            return false
        },

        dialogTitle() {
            let title = `Transfer Phone Numbers `

            if (this.transfer_numbers.from_company) {
                title += ` from ${this.from_company_label}`
            }

            title += ` to ${this.to_company_label}`

            return title
        }
    },

    methods: {
        resetForm() {
            this.loading = false
            this.transfer_numbers = {
                phone_numbers: null,
                from_company: null,
                to_company: null,
                address_sid: null,
                to_campaign: null
            };
        },

        submitForm() {
            if (this.validateForm(this.form_name)) {
                this.loading = true

                const params = {
                    phone_numbers: this.arrayedPhoneNumbers,
                    from_company: this.transfer_numbers.from_company,
                    to_company: this.transfer_numbers.to_company,
                    address_sid: this.transfer_numbers.address_sid,
                    to_campaign: this.transfer_numbers.to_campaign,
                }
                const admin_id = this.auth?.user?.profile?.id || null

                axios.post(`/api/admin/campaigns/${admin_id}/transfer-numbers`, params)
                    .then(res => {
                        this.transfer_result = res.data
                        this.showResultDialog = true
                    })
                    .catch(err => {
                        this.loading = false

                        return this.$notify.error({
                            title: 'Transfer failed',
                            message: err.response.data.message,
                        });
                    })
            }
        },

        setToCompanyLabel(company) {
            this.to_company_label = company.name
        },

        setFromCompanyLabel(company) {
            this.from_company_label = company.name
        },

        fetchCampaigns(company_id) {
            axios.get(`/api/admin/company/${company_id}/campaigns`)
                .then(res => {
                    this.campaigns = res.data
                })
                .catch(err => {
                    this.loading = false

                    this.$notify.error({
                        title: 'Error searching for campaigns',
                        message: err.response.data.message,
                    });
                })
        },
    },

    watch: {
        'transfer_numbers.to_company': function(company_id) {
            this.transfer_numbers.to_campaign = null

            if (company_id) {
                this.fetchCampaigns(company_id)
            } else {
                this.campaigns = []
            }
        }
    }
}
</script>
